import { useCallback, useEffect, useRef, useState } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType } from '@zxing/library'
import { fotosIaApi } from '../../api/fotosIa'
import { colors, font, text } from '../../design/tokens'
import { BotonPrimario, Card, Cargando, ErrorBox, Seccion, Titulo } from './comunes'
import CorreccionFotoIa from '../../components/CorreccionFotoIa'

// Fotos de producto desde el celular con IA (Martin, 2 oct 2026: "vamos a
// hacerlo con gemini"). Escanear el código → elegir el artículo → sacar la
// foto → Gemini la devuelve de estudio (fondo blanco, de frente) → el
// operario la acepta y queda como LA foto del catálogo. Nada se publica solo.
// Lector de códigos: el mismo zxing de Precios ML (Safari no tiene BarcodeDetector).
const HINTS = new Map([[DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E]]])

const errorDe = (e, porDefecto) => {
  const errs = e.response?.data?.errors
  return errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || porDefecto)
}

export default function MovilFotos() {
  const videoRef = useRef(null)
  const controlesRef = useRef(null)
  const camaraRef = useRef(null)
  const [estado, setEstado] = useState(null)
  const [escaneando, setEscaneando] = useState(false)
  const [ean, setEan] = useState('')
  const [sku, setSku] = useState('')
  const [buscado, setBuscado] = useState('')   // el código que se buscó (EAN o SKU), para el "no hay"

  const [buscando, setBuscando] = useState(false)
  const [articulos, setArticulos] = useState(null)
  const [articulo, setArticulo] = useState(null)
  const [procesando, setProcesando] = useState(false)
  const [foto, setFoto] = useState(null)          // resultado de la IA (fila fotos_ia)
  const [imgs, setImgs] = useState({ resultado: null, original: null })
  const [publicada, setPublicada] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    fotosIaApi.estado().then(({ data }) => setEstado(data)).catch(() => setEstado({ activo: false }))
  }, [])

  const parar = useCallback(() => {
    controlesRef.current?.stop()
    controlesRef.current = null
    setEscaneando(false)
  }, [])
  useEffect(() => parar, [parar])

  // Las imágenes llegan como blob: liberar las URLs viejas.
  useEffect(() => () => { Object.values(imgs).forEach(u => u && URL.revokeObjectURL(u)) }, [imgs])

  const limpiarResultado = () => { setFoto(null); setImgs({ resultado: null, original: null }) }

  // Por EAN (escaneo o tipeado) o por SKU (Martin, 14 oct 2026).
  const consultar = useCallback(async (codigo, porSku = false) => {
    if (porSku) setSku(codigo); else setEan(codigo)
    setBuscado(codigo); setBuscando(true); setError(''); setArticulos(null); setArticulo(null); setPublicada(null); limpiarResultado()
    try {
      const { data } = await (porSku ? fotosIaApi.articulosPorSku(codigo) : fotosIaApi.articulos(codigo))
      setArticulos(data.articulos)
      if (data.articulos.length === 1) setArticulo(data.articulos[0])
    } catch (e) {
      setError(errorDe(e, 'No se pudo buscar el artículo.'))
    } finally { setBuscando(false) }
  }, [])

  const escanear = async () => {
    setError(''); setEscaneando(true)
    window.scrollTo(0, 0)
    try {
      const lector = new BrowserMultiFormatReader(HINTS)
      controlesRef.current = await lector.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' } } },
        videoRef.current,
        (resultado, _err, controles) => {
          if (!resultado) return
          controles.stop()
          controlesRef.current = null
          setEscaneando(false)
          if (navigator.vibrate) navigator.vibrate(60)
          consultar(resultado.getText())
        },
      )
    } catch (e) {
      setEscaneando(false)
      setError(e?.name === 'NotAllowedError'
        ? 'El navegador no dio permiso para usar la cámara. Habilitala en Ajustes → Safari → Cámara, o tipeá el código.'
        : 'No se pudo abrir la cámara. Tipeá el código.')
    }
  }

  const manual = (e) => {
    e.preventDefault()
    const codigo = ean.replace(/\D+/g, '')
    if (codigo.length >= 8) consultar(codigo)
  }

  const manualSku = (e) => {
    e.preventDefault()
    const codigo = sku.trim().toUpperCase()
    if (codigo) consultar(codigo, true)
  }

  const alSacarFoto = async (e) => {
    const archivo = e.target.files?.[0]
    e.target.value = ''
    if (!archivo || !articulo) return
    setProcesando(true); setError(''); limpiarResultado()
    try {
      const { data } = await fotosIaApi.procesar(articulo.id, archivo)
      await mostrar(data)
    } catch (err) {
      setError(errorDe(err, 'No se pudo procesar la foto.'))
    } finally { setProcesando(false) }
  }

  const mostrar = async (data) => {
    const [r, o] = await Promise.all([fotosIaApi.imagen(data.id), fotosIaApi.imagen(data.id, true)])
    setImgs({ resultado: URL.createObjectURL(r.data), original: URL.createObjectURL(o.data) })
    setFoto(data)
  }

  const publicar = async () => {
    setProcesando(true); setError('')
    try {
      const { data } = await fotosIaApi.publicar(foto.id)
      setPublicada(data.articulo)
      limpiarResultado()
    } catch (e) {
      setError(errorDe(e, 'No se pudo publicar la foto.'))
    } finally { setProcesando(false) }
  }

  const otraFoto = async () => {
    if (foto) fotosIaApi.descartar(foto.id).catch(() => {})
    limpiarResultado()
    camaraRef.current?.click()
  }

  const activo = !!estado?.activo

  return (
    <div>
      <Titulo>Fotos de productos</Titulo>

      {estado && !activo && (
        <div style={st.aviso}>
          Las fotos con IA están apagadas: falta activar la facturación de Gemini. Podés escanear, pero no procesar fotos.
        </div>
      )}

      <div style={{ ...st.visor, display: escaneando ? 'block' : 'none' }}>
        <video ref={videoRef} style={st.video} muted playsInline />
        <div style={st.guia} />
      </div>
      {escaneando
        ? <button onClick={parar} style={st.btnSec}>Cancelar</button>
        : <BotonPrimario onClick={escanear}>📷 Escanear código</BotonPrimario>}

      <form onSubmit={manual} style={st.manual}>
        <input value={ean} onChange={e => setEan(e.target.value)} placeholder="o tipeá el EAN" inputMode="numeric" style={st.input} />
        <button type="submit" style={{ ...st.btnSec, width: 'auto', flexShrink: 0 }}>Buscar</button>
      </form>
      <form onSubmit={manualSku} style={st.manual}>
        <input value={sku} onChange={e => setSku(e.target.value)} placeholder="o tipeá el SKU" autoCapitalize="characters" autoCorrect="off" spellCheck={false} style={st.input} />
        <button type="submit" style={{ ...st.btnSec, width: 'auto', flexShrink: 0 }}>Buscar</button>
      </form>

      <ErrorBox>{error}</ErrorBox>
      {buscando && <Cargando />}

      {articulos && articulos.length === 0 && (
        <Card style={{ marginTop: 12 }}><p style={st.nota}>No hay artículos con el código {buscado} en LGR.</p></Card>
      )}
      {articulos && articulos.length > 1 && !articulo && (
        <Seccion titulo="¿Cuál de estos es?">
          {articulos.map(a => (
            <button key={a.id} onClick={() => setArticulo(a)} style={st.opcion}>
              <span style={{ minWidth: 0 }}>
                <span style={st.nombre}>{a.descripcion}</span>
                <span style={st.sub}><span style={st.mono}>{a.sku}</span>{a.foto_actual ? ' · tiene foto' : ' · sin foto'}</span>
              </span>
              <span style={st.flecha}>›</span>
            </button>
          ))}
        </Seccion>
      )}

      {publicada && (
        <Card style={{ marginTop: 12 }}>
          <div style={st.ok}>✓ Foto publicada en el catálogo</div>
          <div style={st.fila}>
            {publicada.foto_actual && <img src={publicada.foto_actual} alt="" style={st.miniatura} />}
            <div style={{ minWidth: 0 }}>
              <div style={st.nombre}>{publicada.descripcion}</div>
              <div style={st.sub}><span style={st.mono}>{publicada.sku}</span></div>
            </div>
          </div>
          <BotonPrimario onClick={escanear} style={{ marginTop: 12 }}>📷 Escanear otro</BotonPrimario>
        </Card>
      )}

      {articulo && !publicada && (
        <Card style={{ marginTop: 12 }}>
          <div style={st.fila}>
            {articulo.foto_actual
              ? <img src={articulo.foto_actual} alt="Foto actual" style={st.miniatura} />
              : <div style={{ ...st.miniatura, ...st.sinFoto }}>sin foto</div>}
            <div style={{ minWidth: 0 }}>
              <div style={st.nombre}>{articulo.descripcion}</div>
              <div style={st.sub}><span style={st.mono}>{articulo.sku}</span>{articulo.ean ? <> · <span style={st.mono}>{articulo.ean}</span></> : null}</div>
              {articulo.foto_actual && <div style={st.sub}>Ya tiene foto: la nueva la reemplaza.</div>}
            </div>
          </div>

          <input ref={camaraRef} type="file" accept="image/*" capture="environment" onChange={alSacarFoto} style={{ display: 'none' }} />
          {!foto && !procesando && (
            <BotonPrimario onClick={() => camaraRef.current?.click()} disabled={!activo} style={{ marginTop: 12, opacity: activo ? 1 : 0.5 }}>
              📸 Sacar foto
            </BotonPrimario>
          )}
          {!foto && !procesando && (
            <p style={st.nota}>El producto entero, de frente, con la etiqueta a la vista y buena luz. La IA la devuelve con fondo blanco.</p>
          )}
          {procesando && <div style={st.procesando}>Procesando con IA… puede tardar unos 20 segundos.</div>}

          {foto && imgs.resultado && (
            <>
              <img src={imgs.resultado} alt="Resultado" style={st.resultado} />
              <div style={st.meta}>
                {foto.modelo_nombre} · US${Number(foto.usd).toFixed(3)}{foto.segundos != null ? ` · ${foto.segundos} s` : ''}
              </div>
              <details style={{ marginTop: 6 }}>
                <summary style={st.sub}>Ver la foto original</summary>
                {imgs.original && <img src={imgs.original} alt="Original" style={{ ...st.resultado, marginTop: 6 }} />}
              </details>
              {foto.correccion && <p style={st.nota}>Corrección pedida: «{foto.correccion}»</p>}
              {foto.fondo_ok === false
                ? <div style={{ ...st.aviso, marginTop: 10, marginBottom: 0 }}>La IA devolvió el fondo NO blanco: así no se puede usar. Pedí la corrección (ej. "fondo blanco puro") o sacá otra.</div>
                : <p style={st.nota}>Revisá que la etiqueta diga lo mismo que la botella real antes de usarla.</p>}
              {foto.fondo_ok !== false && (
                <BotonPrimario onClick={publicar} disabled={procesando} style={{ marginTop: 10 }}>✓ Usar esta foto</BotonPrimario>
              )}
              <CorreccionFotoIa fotoId={foto.id} disabled={procesando} onCorregida={(nueva) => mostrar(nueva).catch(() => setError('No se pudo mostrar la foto corregida.'))} />
              <button onClick={otraFoto} disabled={procesando} style={{ ...st.btnSec, marginTop: 8 }}>↻ Sacar otra</button>
            </>
          )}
        </Card>
      )}
    </div>
  )
}

const mono = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' }
const st = {
  mono,
  aviso: { background: colors.warnBg, color: colors.warn, borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 10, fontFamily: font.sans },
  visor: { position: 'relative', borderRadius: 14, overflow: 'hidden', background: colors.codeBg, marginBottom: 10 },
  video: { width: '100%', display: 'block', aspectRatio: '4 / 3', objectFit: 'cover' },
  guia: { position: 'absolute', left: '12%', right: '12%', top: '45%', height: 2, background: colors.errStrong, opacity: 0.8 },
  btnSec: { padding: '13px 16px', borderRadius: 12, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: 15, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer', width: '100%', boxSizing: 'border-box', minHeight: 44 },
  manual: { display: 'flex', gap: 8, marginTop: 10 },
  input: { flex: 1, minWidth: 0, padding: '12px 12px', borderRadius: 12, border: `1px solid ${colors.textPlh}`, fontSize: 16, color: colors.text, background: colors.bg, outline: 'none', boxSizing: 'border-box', ...mono },
  opcion: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, width: '100%', padding: '12px 0', minHeight: 44, border: 'none', borderBottom: `1px solid ${colors.borderSoft}`, background: 'none', textAlign: 'left', cursor: 'pointer' },
  flecha: { color: colors.textMeta, fontSize: 20, flexShrink: 0 },
  fila: { display: 'flex', alignItems: 'center', gap: 12 },
  miniatura: { width: 64, height: 64, objectFit: 'contain', borderRadius: 8, border: `1px solid ${colors.borderSoft}`, background: '#fff', flexShrink: 0 },
  sinFoto: { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: text.meta, color: colors.textMeta, fontFamily: font.sans },
  nombre: { display: 'block', fontSize: text.body, color: colors.text, fontFamily: font.sans },
  sub: { display: 'block', fontSize: text.sec, color: colors.textSec, fontFamily: font.sans },
  nota: { fontSize: text.sec, color: colors.textSec, margin: '8px 0 0', fontFamily: font.sans },
  procesando: { marginTop: 12, padding: '14px 12px', borderRadius: 10, background: colors.bgSoft, color: colors.textSec, fontSize: 13.5, textAlign: 'center', fontFamily: font.sans },
  resultado: { width: '100%', aspectRatio: '1 / 1', objectFit: 'contain', borderRadius: 10, border: `1px solid ${colors.borderSoft}`, background: '#fff', marginTop: 12, display: 'block' },
  meta: { marginTop: 6, fontSize: text.sec, color: colors.textMeta, textAlign: 'center', ...mono },
  ok: { fontSize: 15, fontWeight: 700, color: colors.ok, marginBottom: 10, fontFamily: font.sans },
}
